<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, onMounted, onBeforeMount } from "vue";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import { useUiStore } from "@/stores/ui";
import { useAgentsStore } from "@/stores/agents";
import { useGridPreferencesStore } from "@/stores/gridPreferences";
import { useDataCollectionStore } from "@/stores/dataCollection";
import { useThemesStore } from "@/stores/themes";
import { useMediaStore } from "@/stores/media";
import {
  DxDataGrid,
  DxColumn,
  // DxFilterRow,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSorting,
  DxScrolling,
  DxColumnChooser,
  DxLoadPanel,
  DxStateStoring,
  DxSearchPanel,
} from "devextreme-vue/data-grid";
// import DxDropDownButton from "devextreme-vue/drop-down-button";
// import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import ActionCell from "../components/agents/ActionCell.vue";

import addAgent from "@/components/agents/addAgent.vue";
import editAgent from "@/components/agents/editAgent.vue";
import deleteAgent from "@/components/agents/deleteAgent.vue";
import duplicateAgent from "@/components/agents/duplicateAgent.vue";
import promptHistory from "@/components/agents/promptHistory.vue";
import assignPrompt from "@/components/agents/assignPrompt.vue";
import { useCompanyStore } from "@/stores/company";
import { hasCapability } from "@/utils/accessControl";

const { companyId } = storeToRefs(useCompanyStore());
const adminPreferenceId = computed(() =>
  localStorage.getItem("adminPreferenceId"),
);
// import useValidation from '@/utils/helpers/validations';

// const { checkLoggedInUserRole } = useValidation();
const uiStore = useUiStore();
const agentsStore = useAgentsStore();
const gridPreferencesStore = useGridPreferencesStore();
const dataCollectionStore = useDataCollectionStore();
const themesStore = useThemesStore();
const mediaStore = useMediaStore();
let getModalState = computed(() => uiStore.modalState);
const dataGridRefName = ref(null);
let showClearAll = ref(false);
const allowedOperations = ref(["contains"]);
// const buttonData = ref(["Excel", "CSV", "PDF"]);
let agentsGrid = ref([]);
let agentTypesOptions = ref([]);
let dataCollectionsGridOptions = ref([]);
let voiceOptions = ref([]);
let avatarOptions = ref([]);
let llmModelOptions = ref([]);
let sttModelOptions = ref([]);
let presentationThemeOptions = ref([]);
let presentationImageOptions = ref([]);
const canManageAgents = hasCapability("agents_management");
// const agentModal = (type) => {
//   if (type == "show") state.agentModal.show();
//   else state.agentModal.hide();
// };

const addAgentAction = async (action, data) => {
  if (!canManageAgents) return;
  await uiStore.setModalAction(action);
  uiStore.setModalState(true);
  await uiStore.setModalData(data);
};

const dataGrid = computed(() => {
  return dataGridRefName.value.instance;
});

const contentReady = (e) => {
  let sortedExp = getSortedColumns();
  let filterExp = e.component.getCombinedFilter();
  if (filterExp || sortedExp) {
    showClearAll.value = true;
  } else {
    showClearAll.value = false;
  }
  setColumnChooserPosition();
};

const getSortedColumns = () => {
  let allColumns = Array.from(Array(dataGrid.value.columnCount()).keys()).map(
    (index) => dataGrid.value.columnOption(index),
  );
  allColumns = allColumns.find((col) => col.sortIndex != null);
  return allColumns;
};

const setColumnChooserPosition = () => {
  var columnChooserView = dataGrid.value.getView("columnChooserView");
  if (!columnChooserView._popupContainer) {
    columnChooserView._initializePopupContainer();
    columnChooserView.render();
  }
  columnChooserView._popupContainer.option("position", {
    of: dataGrid.value.element(),
    my: "left top",
    at: "left top",
    offset: "0 50",
  });
};

const onEditorPreparing = (e) => {
  if (e.parentType == "filterRow") {
    e.editorOptions.placeholder = "Search";
  }
};

const clearFilter = () => {
  dataGrid.value.clearFilter("row");
  dataGrid.value.clearFilter("header");
  dataGrid.value.clearFilter("search");
  dataGrid.value.clearSorting();
};

const customLoad = async () => {
  await gridPreferencesStore
    .onTableHeader({
      url: "/agents-data-grid",
      companyId: companyId.value,
    })
    .catch(() => {});
  let value = gridPreferencesStore.tableHeaders;
  return value;
};

const customSave = (gridState) => {
  gridState.pageIndex = 0;
  gridState.pageSize = 10;
  let value = JSON.stringify(gridState);
  value = value.replace(',"selectedRowKeys":[]', "");

  if (!gridPreferencesStore.tableHeaders) {
    gridPreferencesStore
      .createTableHeaders({
        url: "/agents-data-grid",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
      })
      .catch(() => {});
  } else if (value != JSON.stringify(gridPreferencesStore.tableHeaders)) {
    gridPreferencesStore
      .updateTableHeaders({
        url: "/agents-data-grid",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
        adminPreferenceId: adminPreferenceId.value,
      })
      .catch(() => {});
  }};

const refreshGrid = () => {
  getAgentsGrid();
};

const onItemClick = (type) => {
  if (type == "Excel") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "agents.xlsx",
        );
      });
    });
    // e.cancel = true;
  } else if (type == "CSV") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.csv.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "agents.csv",
        );
      });
    });
  } else if (type == "PDF") {
    const doc = new jsPDF("landscape");
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: dataGrid.value,
      customizeCell: function (options) {
        const { gridCell, pdfCell } = options;
        if (gridCell.rowType === "data") {
          // data columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            cellWidth: "auto",
          };
        } else {
          // header columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            fillColor: "#2680EB",
            textColor: "#FFFFFF",
            cellWidth: "auto",
          };
        }
      },
    }).then(() => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        const header = "Agents List";
        const headerWidth = doc.getTextDimensions(header).w;
        const footer = `Page ${i} of ${pageCount}`;

        // Header
        doc.text(header, (pageWidth - headerWidth) / 2, 10);

        // Footer
        doc.text(
          footer,
          pageWidth / 2 - doc.getTextWidth(footer) / 2,
          pageHeight - 5,
          { baseline: "bottom" },
        );
      }

      doc.save("agents.pdf");
    });
  }
};

const fetchAgentTypes = () => {
  agentsStore
    .fetchAgentTypes()
    .then((res) => {
      // Transform object into array format for VMultiselect
      const agentTypesData = res.data;
      agentTypesOptions.value = Object.keys(agentTypesData).map((key) => ({
        name: key,
        description: agentTypesData[key],
      }));
    })
    .catch(() => {});
};

const fetchDataCollections = () => {
  dataCollectionStore
    .fetchDataCollectionsSimple(companyId.value)
    .then((res) => {
      dataCollectionsGridOptions.value = res.data;
    })
    .catch(() => {
    });
};

const getDataCollectionsNames = (data_collections) => {
  // return data_collections.map(dc => dc.name).join(' - ');
  return data_collections.map((dc) => dc.name);
};

const fetchVoices = () => {
  agentsStore
    .fetchAgentVoices()
    .then((res) => {
      voiceOptions.value = res;
    })
    .catch(() => {});
};

const fetchAvatars = () => {
  agentsStore
    .fetchAvatars()
    .then((res) => {
      avatarOptions.value = res;
    })
    .catch(() => {});
};

const fetchLlmModels = () => {
  agentsStore
    .fetchLlmModels()
    .then((res) => {
      llmModelOptions.value = res;
    })
    .catch(() => {});
};

const fetchSttModels = () => {
  agentsStore
    .fetchSttModels()
    .then((res) => {
      sttModelOptions.value = res;
    })
    .catch(() => {});
};

const fetchPresentationOptions = () => {
  themesStore.fetchThemesGrid(companyId.value).then((res) => {
    presentationThemeOptions.value = (res.data || []).map((theme) => ({
      label: theme.name || theme.id,
      value: theme.id,
      primary: theme.properties?.palette?.brand_primary || null,
      secondary: theme.properties?.palette?.header?.header_color
        || theme.properties?.palette?.text_secondary
        || null,
    }));
  }).catch(() => {});

  mediaStore.fetchAllMedia(companyId.value).then((res) => {
    presentationImageOptions.value = (res.data || [])
      .filter((media) => (media.type || media.media_type) === "image")
      .map((media) => ({ label: media.name || media.id, value: media.id }));
  }).catch(() => {});
};


const getAgentsGrid = () => {
  agentsStore
    .fetchAgentsGrid(companyId.value)
    .then((res) => {
      dataGrid.value.endCustomLoading();
      agentsGrid.value = res.data.map((row) => {
        row.status_text =
          row.status.charAt(0).toUpperCase() +
          row.status.slice(1).toLowerCase();
        row.agent_type_text = row.agentTypeInfo
          ? row.agentTypeInfo?.description
          : "-";
        row.data_collection_text = getDataCollectionsNames(
          row.data_collections,
        );
        return row;
      });
    })
    .catch(() => {
    });
};

onMounted(() => {
  dataGrid.value.beginCustomLoading();
});

onBeforeMount(() => {
  fetchDataCollections();
  fetchAgentTypes();
  fetchVoices();
  fetchAvatars();
  fetchLlmModels();
  fetchSttModels();
  fetchPresentationOptions();
  getAgentsGrid();
});
</script>

<template>
  <AgentLayout>
    <PageHeader
      title="Agents List"
      subtitle="Manage your agents, set their type and status, and link multiple data sources to assign them to any device."
      :showButton="canManageAgents"
      buttonText="Add"
      @action-click="addAgentAction('add_agent', {})"
    />

    <addAgent
      v-if="getModalState"
      :agentTypesOptions="agentTypesOptions"
      :dataCollectionsGridOptions="dataCollectionsGridOptions"
      :voiceOptions="voiceOptions"
      :avatarOptions="avatarOptions"
      :llmModelOptions="llmModelOptions"
      :sttModelOptions="sttModelOptions"
      :presentationThemeOptions="presentationThemeOptions"
      :presentationImageOptions="presentationImageOptions"
      @refresh="refreshGrid"
    />
    <editAgent
      v-if="getModalState"
      :agentTypesOptions="agentTypesOptions"
      :dataCollectionsGridOptions="dataCollectionsGridOptions"
      :voiceOptions="voiceOptions"
      :avatarOptions="avatarOptions"
      :llmModelOptions="llmModelOptions"
      :sttModelOptions="sttModelOptions"
      :presentationThemeOptions="presentationThemeOptions"
      :presentationImageOptions="presentationImageOptions"
      @refresh="refreshGrid"
    />
    <deleteAgent v-if="getModalState" @refresh="refreshGrid" />
    <duplicateAgent v-if="getModalState" @refresh="refreshGrid" />
    <promptHistory v-if="getModalState" />
    <assignPrompt v-if="getModalState" @refresh="refreshGrid" />

    <section class="grid-white-section main-page-container-animation card">
      <DxDataGrid height="100%"
        :data-source="agentsGrid"
        ref="dataGridRefName"
        :remote-operations="true"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="false"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="'No agents yet'"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
      >
        <DxLoadPanel :show-indicator="true" :show-pane="true" :enabled="true" />
        <DxScrolling mode="standard" :use-native="true" />
        <DxPaging :page-size="10" />
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxPager
          :visible="true"
          :allowed-page-sizes="[10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />

        <!-- <DxFilterRow :visible="true" /> -->
        <DxSearchPanel :width="200" :visible="true" placeholder="Search ..." />
        <DxSorting mode="single" />
        <DxHeaderFilter :visible="true" />
        <DxColumnChooser :enabled="true" mode="select" />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
          <DxItem location="after" name="searchPanel" />
        </DxToolbar>

        <DxColumn
          data-field="name"
          caption="Name"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="data_collection_text"
          caption="Data Collections"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
          :max-width="300"
          css-class="white-space-break-spaces"
          :width="250"
          cell-template="dataCollectionCellTemplate"
        />

        <DxColumn
          data-field="agent_type_text"
          caption="Agent Type"
          :allow-header-filtering="true"
          :allow-searching="true"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="status_text"
          caption="Status"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
          cell-template="statusCellTemplate"
        />

        <DxColumn
          data-field="actions"
          caption="Actions"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          :allow-sorting="false"
          cell-template="action-cell"
          data-type="object"
          :min-width="100"
        />

        <template #dataCollectionCellTemplate="{ data }">
          <!-- <ul> -->
          <span
            v-for="item in data.value"
            :key="item.index"
            class="status-badge status-grid-chip"
            >{{ item }}</span
          >
          <!-- </ul> -->
        </template>

        <template #action-cell="{ data }">
          <ActionCell :data="data.data" />
        </template>

        <template #statusCellTemplate="{ data }">
          <span class="status-badge" :class="`status-${data.data.status}`">{{
            data.value
          }}</span>
        </template>

        <!-- <template #columnButtonTemplate>
            <button class="custom-columns mx-1" @click="dataGrid.showColumnChooser()">
              <span class="custom-text">Columns </span>
              <span class="custom-icon">
                <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
              </span>
            </button>
          </template>

          <template #exportButtonTemplate>
            <DxDropDownButton class="exportGridHeader" :items="buttonData" text="Export" @item-click="onItemClick" />
          </template>

          <template #clearAllTemplate>
            <div v-if="showClearAll" class="clear-cols">
              <div class="custom-line"></div>
              <Button
                text="Clear All"
                @click="clearFilter()"
                styling-mode="outlined"
                type="danger"
                class="clear-colums-filter"
              />
            </div>
          </template> -->

        <template #exportButtonTemplate>
          <button
            @click="onItemClick('Excel')"
            type="button"
            class="btn icon-button m-5 font-20"
          >
            <i class="bi bi-filetype-xlsx"></i>
          </button>
        </template>

        <template #columnButtonTemplate>
          <button
            @click="dataGrid.showColumnChooser()"
            type="button"
            class="btn icon-button font-20 m-5"
          >
            <i class="bi bi-columns"></i>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols">
            <div class="custom-line"></div>
            <button
              @click="clearFilter()"
              type="button"
              class="btn action-btn clear-colums-filter m-5"
            >
              Clear Filters
            </button>
          </div>
        </template>
      </DxDataGrid>
    </section>
  </AgentLayout>
</template>
